import { Flex } from 'foundations-components/components/Flex';
import { MultiColumn } from 'foundations-components/transitional/components/MultiColumn';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { Text } from 'foundations-components/components/Text';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useMemo } from 'react';
import { AppPermission } from './AppPermission';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { useRemoveAppPermissionsFromInstalls } from '../../hooks/mutations/useRemoveAppPermissionsFromInstalls';
import { useApp } from '../../contexts/AppContext';
import { isPermissionDisabled } from '../../utils/dataPermissions/isPermissionDisabled';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function BasePermissionsListLayout({
  title,
  permissions,
  children
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "300",
    children: [title, children, /*#__PURE__*/_jsx(MultiColumn, {
      children: permissions.map(permission => /*#__PURE__*/_jsx(AppPermission, {
        permission: permission,
        type: "required"
      }, permission.id))
    })]
  });
}
export function RequiredAppPermissionsList({
  permissions
}) {
  if (permissions.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "300",
    "data-selenium": "data-permissions-required-list",
    children: [/*#__PURE__*/_jsxs(Text, {
      as: "h5",
      marginBlockEnd: "0",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.dataPermissions.permissionsList.required"
      }), /*#__PURE__*/_jsx(Text, {
        as: "span",
        color: "text-core-subtle",
        marginInlineStart: "100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.dataPermissions.permissionsList.requiredCount",
          options: {
            count: permissions.length
          }
        })
      })]
    }), /*#__PURE__*/_jsx(MultiColumn, {
      children: permissions.map(permission => /*#__PURE__*/_jsx(AppPermission, {
        permission: permission,
        type: "required"
      }, permission.id))
    })]
  });
}
export function RemovedAppPermissionsList({
  permissions
}) {
  const {
    appId
  } = useApp();
  const {
    removeAppPermissionsFromInstalls,
    loading,
    error
  } = useRemoveAppPermissionsFromInstalls();
  const handleRemovePermissions = useCallback(() => {
    const appPermissionIds = permissions.map(permission => permission.id);
    void removeAppPermissionsFromInstalls({
      variables: {
        appId,
        appPermissionIds
      }
    });
  }, [appId, permissions, removeAppPermissionsFromInstalls]);
  if (permissions.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(BasePermissionsListLayout, {
    title: /*#__PURE__*/_jsxs(Text, {
      as: "h5",
      marginBlockEnd: "0",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.dataPermissions.permissionsList.removed"
      }), /*#__PURE__*/_jsx(Text, {
        as: "span",
        color: "text-core-subtle",
        marginInlineStart: "100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.dataPermissions.permissionsList.removedCount",
          options: {
            count: permissions.length
          }
        })
      })]
    }),
    permissions: permissions,
    children: /*#__PURE__*/_jsx(Alert, {
      type: "warning",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.dataPermissions.removedAlert.title"
      }),
      children: /*#__PURE__*/_jsxs(ButtonWrapper, {
        children: [/*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.dataPermissions.removedAlert.description"
          })
        }), /*#__PURE__*/_jsx(LoadingButton, {
          size: "extra-small",
          use: "tertiary",
          failed: !!error,
          loading: loading,
          onClick: handleRemovePermissions,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.dataPermissions.removedAlert.button"
          })
        })]
      })
    })
  });
}
function SelectableAppPermissionsList({
  permissions,
  changedPermissions,
  setChangedPermissions,
  permissionType,
  renderTitle,
  'data-selenium': dataSelenium
}) {
  const initialPermissionsMap = useMemo(() => permissions.reduce((acc, permission) => {
    acc[permission.id] = permission.isAllowed;
    return acc;
  }, {}), [permissions]);
  const getInitialValue = useCallback(permissionId => {
    var _initialPermissionsMa;
    return (_initialPermissionsMa = initialPermissionsMap[permissionId]) !== null && _initialPermissionsMa !== void 0 ? _initialPermissionsMa : false;
  }, [initialPermissionsMap]);
  const getCurrentValue = useCallback(permissionId => {
    if (permissionId in changedPermissions) {
      return changedPermissions[permissionId];
    }
    return getInitialValue(permissionId);
  }, [changedPermissions, getInitialValue]);
  const selectedCount = useMemo(() => permissions.filter(permission => getCurrentValue(permission.id)).length, [permissions, getCurrentValue]);
  const totalCount = permissions.length;
  const handlePermissionChange = useCallback((permissionId, checked) => {
    setChangedPermissions(prev => {
      const initialValue = getInitialValue(permissionId);
      const updated = Object.assign({}, prev);
      if (checked === initialValue) {
        delete updated[permissionId];
      } else {
        updated[permissionId] = checked;
      }
      return updated;
    });
  }, [setChangedPermissions, getInitialValue]);
  const handleSetAll = useCallback(getTargetState => {
    const updated = {};
    permissions.forEach(permission => {
      const {
        id: permissionId
      } = permission;
      const initialValue = getInitialValue(permissionId);
      // Any reasons why the permission is disabled should apply here. These permissions should not be modified.
      const isDisabled = isPermissionDisabled(permissionType, permission);
      const targetState = getTargetState(permission);
      if (!isDisabled && initialValue !== targetState) {
        updated[permissionId] = targetState;
      }
    });
    setChangedPermissions(updated);
  }, [setChangedPermissions, permissions, getInitialValue, permissionType]);
  const handleEnableAll = useCallback(() => handleSetAll(() => true), [handleSetAll]);
  const handleDisableAll = useCallback(() => handleSetAll(() => false), [handleSetAll]);
  const handleViewOnly = useCallback(() => handleSetAll(permission => permission.readOnly === true), [handleSetAll]);
  const hasWritePermissions = useMemo(() => permissions.some(permission => !permission.readOnly), [permissions]);
  if (permissions.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "300",
    "data-selenium": dataSelenium,
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      children: [renderTitle({
        selectedCount,
        totalCount
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "detail-100",
          color: "text-core-subtle",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.dataPermissions.permissionsList.quickSelect.label"
          })
        }), /*#__PURE__*/_jsxs(ButtonGroup, {
          children: [/*#__PURE__*/_jsx(Button, {
            use: "tertiary-light",
            size: "extra-small",
            onClick: handleEnableAll,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app-governance-lib.dataPermissions.permissionsList.button.enableAll"
            })
          }), hasWritePermissions && /*#__PURE__*/_jsx(Button, {
            use: "tertiary-light",
            size: "extra-small",
            onClick: handleViewOnly,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app-governance-lib.dataPermissions.permissionsList.button.viewOnly"
            })
          }), /*#__PURE__*/_jsx(Button, {
            use: "tertiary-light",
            size: "extra-small",
            onClick: handleDisableAll,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app-governance-lib.dataPermissions.permissionsList.button.disableAll"
            })
          })]
        })]
      })]
    }), /*#__PURE__*/_jsx(MultiColumn, {
      children: permissions.map(permission => /*#__PURE__*/_jsx(AppPermission, {
        permission: permission,
        type: permissionType,
        checked: getCurrentValue(permission.id),
        onChange: checked => handlePermissionChange(permission.id, checked)
      }, permission.id))
    })]
  });
}
export function ConditionallyRequiredAppPermissionsList(props) {
  return /*#__PURE__*/_jsx(SelectableAppPermissionsList, Object.assign({}, props, {
    "data-selenium": "data-permissions-conditionally-required-list",
    permissionType: "conditionallyRequired",
    renderTitle: ({
      selectedCount,
      totalCount
    }) => /*#__PURE__*/_jsxs(Text, {
      as: "h5",
      marginBlockEnd: "0",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.dataPermissions.permissionsList.conditionallyRequired"
      }), /*#__PURE__*/_jsx(Text, {
        as: "span",
        color: "text-core-subtle",
        marginInlineStart: "100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.dataPermissions.permissionsList.conditionallyRequiredCount",
          options: {
            selectedCount,
            totalCount
          }
        })
      })]
    })
  }));
}
export function OptionalAppPermissionsList(props) {
  return /*#__PURE__*/_jsx(SelectableAppPermissionsList, Object.assign({}, props, {
    "data-selenium": "data-permissions-optional-list",
    permissionType: "optional",
    renderTitle: ({
      selectedCount,
      totalCount
    }) => /*#__PURE__*/_jsxs(Text, {
      as: "h5",
      marginBlockEnd: "0",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.dataPermissions.permissionsList.optional"
      }), /*#__PURE__*/_jsx(Text, {
        as: "span",
        color: "text-core-subtle",
        marginInlineStart: "100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.dataPermissions.permissionsList.optionalCount",
          options: {
            selectedCount,
            totalCount
          }
        })
      })]
    })
  }));
}